import { DialogCloseEvent } from './dialog.js';
import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1.5;
const DESKTOP_HOVER_ZOOM = 2.3;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];

  #controller = new AbortController();

  /** @type {number} */
  #scale = DEFAULT_ZOOM;

  /** @type {number} */
  #initialDistance = 0;

  /** @type {number} */
  #startScale = DEFAULT_ZOOM;

  /** @type {Point} */
  #translate = { x: 0, y: 0 };

  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };

  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };

  /** @type {boolean} */
  #isDragging = false;

  /** @type {boolean} */
  #initialized = false;

  /** @type {number | null} */
  #animationFrame = null;

  /** @type {number} */
  #lastTapTime = 0;

  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /** @type {boolean} */
  #isHoverPanActive = false;

  /** @type {boolean} */
  #hoverPanInitialized = false;

  get #image() {
    return this.refs.image;
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.#image) return;

    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }

    this.#initResizeListener();

    window.addEventListener(
      DialogCloseEvent.eventName,
      this.#resetZoom
    );

    if (isMobileBreakpoint()) {
      this.#initEventListeners();
    } else {
      this.#initHoverPanListeners();
    }

    this.#updateTransform();
  }

  #initHoverPanListeners() {
    if (this.#hoverPanInitialized) return;

    this.#hoverPanInitialized = true;

    const { signal } = this.#controller;

    this.addEventListener(
      'mouseenter',
      this.#handleMouseEnter,
      { signal }
    );

    this.addEventListener(
      'mousemove',
      this.#handleMouseMove,
      { signal }
    );

    this.addEventListener(
      'mouseleave',
      this.#handleMouseLeave,
      { signal }
    );
  }

  #handleMouseEnter = (event) => {
    if (isMobileBreakpoint()) return;

    this.#isHoverPanActive = true;

    this.#scale = DESKTOP_HOVER_ZOOM;

    this.#updateHoverPanFromPointer(event);
  };

  #handleMouseMove = (event) => {
    if (isMobileBreakpoint()) return;

    if (!this.#isHoverPanActive) {
      this.#isHoverPanActive = true;
      this.#scale = DESKTOP_HOVER_ZOOM;
    }

    this.#updateHoverPanFromPointer(event);
  };

  #updateHoverPanFromPointer(event) {
    if (!this.#image || this.#scale <= MIN_ZOOM) return;

    const rect = this.getBoundingClientRect();

    if (!rect.width || !rect.height) return;

    const ratioX = clamp(
      (event.clientX - rect.left) / rect.width,
      0,
      1
    );

    const ratioY = clamp(
      (event.clientY - rect.top) / rect.height,
      0,
      1
    );

    const offsetX = ratioX - 0.5;
    const offsetY = ratioY - 0.5;

    const scaledWidth =
      rect.width * this.#scale;

    const scaledHeight =
      rect.height * this.#scale;

    const maxX =
      Math.max(
        0,
        (scaledWidth - rect.width) / 2 / this.#scale
      );

    const maxY =
      Math.max(
        0,
        (scaledHeight - rect.height) / 2 / this.#scale
      );

    this.#translate.x =
      -offsetX * 2 * maxX;

    this.#translate.y =
      -offsetY * 2 * maxY;

    this.#requestUpdateTransform();
  }

  #handleMouseLeave = () => {
    if (isMobileBreakpoint()) return;

    this.#isHoverPanActive = false;

    this.#scale = DEFAULT_ZOOM;

    this.#translate.x = 0;
    this.#translate.y = 0;

    this.#requestUpdateTransform();
  };

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;

    this.#initialized = true;

    const { signal } = this.#controller;

    const options = {
      passive: false,
      signal,
    };

    this.addEventListener(
      'touchstart',
      this.#handleTouchStart,
      options
    );

    this.addEventListener(
      'touchmove',
      this.#handleTouchMove,
      options
    );

    this.addEventListener(
      'touchend',
      this.#handleTouchEnd,
      options
    );

    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener(
      DialogCloseEvent.eventName,
      this.#resetZoom
    );

    this.#controller.abort();

    this.#resizeObserver.disconnect();

    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (isMobileBreakpoint()) {
      if (!this.#initialized) {
        this.#initEventListeners();
      }

      if (this.#isHoverPanActive) {
        this.#isHoverPanActive = false;

        this.#scale = DEFAULT_ZOOM;

        this.#translate.x = 0;
        this.#translate.y = 0;
      }
    } else if (!this.#hoverPanInitialized) {
      this.#initHoverPanListeners();
    }

    this.#requestUpdateTransform();
  };

  #resizeObserver =
    new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    preventDefault(event);

    const touchCount =
      event.touches.length;

    if (touchCount === 2) {
      const touch1 =
        event.touches[0];

      const touch2 =
        event.touches[1];

      if (!touch1 || !touch2) return;

      this.#startZoomGestureFromTouches(
        touch1,
        touch2
      );
    } else if (touchCount === 1) {
      const touch =
        event.touches[0];

      if (!touch) return;

      const currentTime =
        performance.now();

      const timeSinceLastTap =
        currentTime - this.#lastTapTime;

      if (
        timeSinceLastTap >=
        DOUBLE_TAP_DELAY
      ) {
        this.#storeTapInfo(
          currentTime,
          touch
        );

        this.#startDragGestureFromTouch(
          touch
        );

        return;
      }

      if (this.#lastTapPosition) {
        const distance =
          getDistance(
            touch,
            this.#lastTapPosition
          );

        if (
          distance <
          DOUBLE_TAP_DISTANCE
        ) {
          this.#handleDoubleTapFromTouch(
            touch
          );

          this.#lastTapTime = 0;
          this.#lastTapPosition = null;

          return;
        }
      }

      this.#storeTapInfo(
        currentTime,
        touch
      );

      this.#startDragGestureFromTouch(
        touch
      );
    }
  };

  #startZoomGestureFromTouches(
    touch1,
    touch2
  ) {
    this.#initialDistance =
      getDistance(touch1, touch2);

    this.#startScale =
      this.#scale;

    this.#isDragging =
      false;
  }

  #startDragGestureFromTouch(
    touch
  ) {
    this.#startPosition = {
      x: touch.clientX,
      y: touch.clientY,
    };

    this.#startTranslate = {
      x: this.#translate.x,
      y: this.#translate.y,
    };

    this.#isDragging = true;

    this.#hasDraggedBeyondThreshold =
      false;
  }

  #storeTapInfo(
    currentTime,
    touch
  ) {
    this.#lastTapTime =
      currentTime;

    this.#lastTapPosition = {
      x: touch.clientX,
      y: touch.clientY,
    };
  }

  #handleDoubleTapFromTouch(
    touch
  ) {
    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    let targetZoom;

    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM;

      this.#hasManualZoom =
        false;

      this.#translate = {
        x: 0,
        y: 0,
      };
    } else {
      const tolerance = 0.05;

      if (
        Math.abs(
          this.#scale -
          MIN_ZOOM
        ) < tolerance
      ) {
        targetZoom =
          DEFAULT_ZOOM;
      } else {
        targetZoom =
          MIN_ZOOM;
      }
    }

    if (
      targetZoom !==
      MIN_ZOOM
    ) {
      const oldScale =
        this.#scale;

      this.#scale =
        Math.min(
          MAX_ZOOM,
          targetZoom
        );

      const distanceFromCenter = {
        x:
          touch.clientX -
          containerCenter.x,

        y:
          touch.clientY -
          containerCenter.y,
      };

      const scaleDelta =
        this.#scale /
          oldScale -
        1.0;

      this.#translate.x -=
        (distanceFromCenter.x *
          scaleDelta) /
        this.#scale;

      this.#translate.y -=
        (distanceFromCenter.y *
          scaleDelta) /
        this.#scale;
    } else {
      this.#scale =
        targetZoom;

      this.#translate = {
        x: 0,
        y: 0,
      };
    }

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    preventDefault(event);

    const touchCount =
      event.touches.length;

    if (touchCount === 2) {
      const touch1 =
        event.touches[0];

      const touch2 =
        event.touches[1];

      if (touch1 && touch2) {
        this.#processZoomGesture(
          touch1,
          touch2
        );
      }
    } else if (
      touchCount === 1 &&
      this.#isDragging
    ) {
      const touch =
        event.touches[0];

      if (touch) {
        this.#processDragGesture(
          touch
        );
      }
    }
  };

  #processZoomGesture(
    touch1,
    touch2
  ) {
    const midX =
      (touch1.clientX +
        touch2.clientX) /
      2;

    const midY =
      (touch1.clientY +
        touch2.clientY) /
      2;

    const currentDistance =
      getDistance(
        touch1,
        touch2
      );

    const oldScale =
      this.#scale;

    const newScale =
      (currentDistance /
        this.#initialDistance) *
      this.#startScale;

    this.#scale =
      clamp(
        newScale,
        MIN_ZOOM,
        MAX_ZOOM
      );

    this.#hasManualZoom =
      true;

    const containerCenterX =
      this.clientWidth / 2;

    const containerCenterY =
      this.clientHeight / 2;

    const distanceFromCenterX =
      midX -
      containerCenterX;

    const distanceFromCenterY =
      midY -
      containerCenterY;

    const scaleDelta =
      this.#scale /
        oldScale -
      1.0;

    this.#translate.x -=
      (distanceFromCenterX *
        scaleDelta) /
      this.#scale;

    this.#translate.y -=
      (distanceFromCenterY *
        scaleDelta) /
      this.#scale;

    this.#requestUpdateTransform();

    this.#isDragging =
      false;
  }

  #processDragGesture(
    touch
  ) {
    const distance =
      getDistance(
        touch,
        this.#startPosition
      );

    if (
      !this
        .#hasDraggedBeyondThreshold &&
      distance <
        DRAG_THRESHOLD
    ) {
      return;
    }

    this.#hasDraggedBeyondThreshold =
      true;

    const dx =
      touch.clientX -
      this.#startPosition.x;

    const dy =
      touch.clientY -
      this.#startPosition.y;

    this.#translate.x =
      this.#startTranslate.x +
      dx / this.#scale;

    this.#translate.y =
      this.#startTranslate.y +
      dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (
      event.touches.length === 0
    ) {
      this.#isDragging =
        false;

      this.#requestUpdateTransform();

      this.#hasDraggedBeyondThreshold =
        false;
    }
  };

  #constrainTranslation() {
    const containerWidth =
      this.clientWidth;

    const containerHeight =
      this.clientHeight;

    if (
      !containerWidth ||
      !containerHeight ||
      !this.#image
    ) {
      return;
    }

    this.#scale =
      clamp(
        this.#scale,
        MIN_ZOOM,
        MAX_ZOOM
      );

    if (
      this.#scale <=
      MIN_ZOOM
    ) {
      this.#translate.x = 0;
      this.#translate.y = 0;

      return;
    }

    const wrapperRect =
      this.getBoundingClientRect();

    const imageElement =
      this.#image;

    let naturalWidth;
    let naturalHeight;

    if (
      imageElement.naturalWidth >
        0 &&
      imageElement.naturalHeight >
        0
    ) {
      naturalWidth =
        imageElement.naturalWidth;

      naturalHeight =
        imageElement.naturalHeight;
    } else {
      naturalWidth =
        wrapperRect.width;

      naturalHeight =
        wrapperRect.width;
    }

    const imageAspectRatio =
      naturalWidth /
      naturalHeight;

    const wrapperAspectRatio =
      wrapperRect.width /
      wrapperRect.height;

    let actualImageWidth;
    let actualImageHeight;

    if (
      imageAspectRatio >
      wrapperAspectRatio
    ) {
      actualImageWidth =
        wrapperRect.width;

      actualImageHeight =
        actualImageWidth /
        imageAspectRatio;
    } else {
      actualImageHeight =
        wrapperRect.height;

      actualImageWidth =
        actualImageHeight *
        imageAspectRatio;
    }

    const scaledImageWidth =
      actualImageWidth *
      this.#scale;

    const scaledImageHeight =
      actualImageHeight *
      this.#scale;

    const horizontalOverflow =
      Math.max(
        0,
        scaledImageWidth -
          wrapperRect.width
      );

    const verticalOverflow =
      Math.max(
        0,
        scaledImageHeight -
          wrapperRect.height
      );

    const maxTranslateX =
      horizontalOverflow /
      2 /
      this.#scale;

    const maxTranslateY =
      verticalOverflow /
      2 /
      this.#scale;

    this.#translate.x =
      clamp(
        this.#translate.x,
        -maxTranslateX,
        maxTranslateX
      );

    this.#translate.y =
      clamp(
        this.#translate.y,
        -maxTranslateY,
        maxTranslateY
      );

    this.style.setProperty(
      '--drag-zoom-scale',
      this.#scale.toString()
    );

    this.style.setProperty(
      '--drag-zoom-translate-x',
      `${this.#translate.x}px`
    );

    this.style.setProperty(
      '--drag-zoom-translate-y',
      `${this.#translate.y}px`
    );
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame =
        requestAnimationFrame(
          this.#updateTransform
        );
    }
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(
        this.#animationFrame
      );

      this.#animationFrame =
        null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame =
      null;

    this.#constrainTranslation();

    this.style.setProperty(
      '--drag-zoom-scale',
      this.#scale.toString()
    );

    this.style.setProperty(
      '--drag-zoom-translate-x',
      `${this.#translate.x}px`
    );

    this.style.setProperty(
      '--drag-zoom-translate-y',
      `${this.#translate.y}px`
    );
  };

  #resetZoom = () => {
    this.#scale =
      DEFAULT_ZOOM;

    this.#startScale =
      DEFAULT_ZOOM;

    this.#translate.x = 0;
    this.#translate.y = 0;

    this.#startPosition = {
      x: 0,
      y: 0,
    };

    this.#startTranslate = {
      x: 0,
      y: 0,
    };

    this.#isDragging =
      false;

    this.#lastTapTime = 0;

    this.#lastTapPosition =
      null;

    this.#hasDraggedBeyondThreshold =
      false;

    this.#isHoverPanActive =
      false;

    this.style.setProperty(
      '--drag-zoom-scale',
      DEFAULT_ZOOM.toString()
    );

    this.style.setProperty(
      '--drag-zoom-translate-x',
      '0px'
    );

    this.style.setProperty(
      '--drag-zoom-translate-y',
      '0px'
    );
  };

  destroy() {
    this.#controller.abort();

    this.#cancelAnimationFrame();
  }
}

function getDistance(
  point1,
  point2
) {
  const x1 =
    /** @type {Point} */ (
      point1
    ).x ??
    /** @type {Touch} */ (
      point1
    ).clientX;

  const y1 =
    /** @type {Point} */ (
      point1
    ).y ??
    /** @type {Touch} */ (
      point1
    ).clientY;

  const x2 =
    /** @type {Point} */ (
      point2
    ).x ??
    /** @type {Touch} */ (
      point2
    ).clientX;

  const y2 =
    /** @type {Point} */ (
      point2
    ).y ??
    /** @type {Touch} */ (
      point2
    ).clientY;

  const dx =
    x1 - x2;

  const dy =
    y1 - y2;

  return Math.sqrt(
    dx * dx +
      dy * dy
  );
}

if (
  !customElements.get(
    'drag-zoom-wrapper'
  )
) {
  customElements.define(
    'drag-zoom-wrapper',
    DragZoomWrapper
  );
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close
 */